import FadeOutToBlack from "@src/components/libraryItemDetailPages/course/FadeOutToBlack";
import useSwipeUpAndDownBetweenScreens, {
  ScrollScreenSwitcherEventHandlerArgs,
} from "@src/components/libraryItemDetailPages/course/hooks/useSwipeUpAndDownBetweenScreens";
import { deprecatedTones } from "@src/deprecatedDesignSystem/styles/deprecatedColors";
import AutoLayout from "@src/deprecatedDesignSystem/components/AutoLayout";
import { HeightAndWidth } from "@src/hooks/useRefHeightAndWidth";
import { Variants, motion } from "framer-motion";
import React, { FC, useMemo, useState } from "react";
import Spinner from "@src/deprecatedDesignSystem/components/Spinner";
import usePhonePreviewScaleFactor from "@src/components/ui/phonePreview/usePhonePreviewScaleFactor";
import { shadows } from "@src/deprecatedDesignSystem/styles/shadows";
import { cn } from "@src/ui/lib/utils";

export const DEFAULT_PHONE_HEIGHT = 784 * 0.95;
export const DEFAULT_PHONE_WIDTH = 375 * 0.95;
export const DEFAULT_PHONE_VERTICAL_PADDING = 10;
const PHONE_BORDER_RADIUS = 32;

type Props = {
  content?: React.ReactNode;
  isSelected: boolean;
  isHovered?: boolean;
  parentDimensions: HeightAndWidth;
  parentVerticalPadding: number;
  animationKey?: string;
  animationScrollDirection?: ScrollDirection;
  onOverscrollUp?: () => void;
  onOverscrollDown?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  phoneWidth?: number;
  phoneHeight?: number;
  hideFadeOutToBlack?: boolean;
  loading?: boolean;
  style?: React.CSSProperties;
  showAiBorder?: boolean;
  showBorder?: boolean;
};

export type ScrollDirection = "up" | "down" | "none";

const PhoneOutline: FC<Props> = (props) => {
  const { showBorder = true } = props;
  const phoneHeight = props.phoneHeight ?? DEFAULT_PHONE_HEIGHT;
  const phoneWidth = props.phoneWidth ?? DEFAULT_PHONE_WIDTH;
  const scale = usePhonePreviewScaleFactor(
    props.parentDimensions,
    props.parentVerticalPadding,
    phoneHeight,
    phoneWidth,
  );
  const directionInt = useMemo(() => {
    if (props.animationScrollDirection === "up") return -1;
    if (props.animationScrollDirection === "down") return 1;
    return 0;
  }, [props.animationScrollDirection]);
  const [lastShiftedScreenAt, setLastShiftedScreenAt] = useState(new Date());
  const [lastScrollEventAt, setLastScrollEventAt] = useState(new Date());
  const verticalAnimationMagnitude = useMemo(() => phoneHeight, [phoneHeight]);
  const animationVariants: Variants = useMemo(
    () => ({
      enter: (direction: number) => {
        if (direction === 0)
          return {
            y: 0,
          };
        return {
          y: verticalAnimationMagnitude * direction,
        };
      },
      center: {
        zIndex: 1,
        y: 0,
      },
      exit: (direction: number) => {
        if (direction === 0)
          return {
            y: 0,
          };
        return {
          zIndex: 0,
          y: verticalAnimationMagnitude * -direction,
        };
      },
    }),
    [verticalAnimationMagnitude],
  );

  return (
    <AutoLayout data-testid="phone-outline">
      <AutoLayout
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          minWidth: phoneWidth * scale,
          maxWidth: phoneWidth * scale,
          height: phoneHeight * scale,
          minHeight: phoneHeight * scale,
          maxHeight: phoneHeight * scale,
          width: phoneWidth * scale,
        }}
      >
        <AutoLayout
          style={{
            userSelect: props.disabled ? "none" : undefined,
            minWidth: phoneWidth,
            maxWidth: phoneWidth,
            height: phoneHeight,
            minHeight: phoneHeight,
            maxHeight: phoneHeight,
            width: phoneWidth,
            borderRadius: PHONE_BORDER_RADIUS,
            flex: 1,
            alignSelf: "stretch",
            backgroundColor: showBorder ? deprecatedTones.white : undefined,
            overflowX: "hidden",
            overflowY: "hidden",
            position: "relative",
            boxShadow: showBorder ? shadows.phoneOutline : undefined,
            ...props.style,
          }}
          id={"phone-outline-outer"}
        >
          <motion.div
            key={props.animationKey}
            custom={directionInt}
            variants={animationVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={PHONE_OUTLINE_ANIMATION_TRANSITION}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignSelf: "stretch",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {props.loading ? (
              <Spinner />
            ) : (
              <PhoneOutlineInner
                key={props.animationKey}
                {...props}
                lastShiftedScreenAt={lastShiftedScreenAt}
                setLastShiftedScreenAt={setLastShiftedScreenAt}
                lastScrollEventAt={lastScrollEventAt}
                setLastScrollEventAt={setLastScrollEventAt}
                onShouldGoToNextScreen={props.onOverscrollDown}
                onShouldGoToPrevScreen={props.onOverscrollUp}
              />
            )}
          </motion.div>
          {!props.isSelected && props.isHovered && (
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: deprecatedTones.gray3Alpha,
                zIndex: 1000,
              }}
            />
          )}
          <div
            className={cn(
              "absolute inset-0 pointer-events-none z-[1000] border-4 border-[rgba(0,0,0,0)]",
              props.showAiBorder && "animate-ai-border",
              props.isSelected && "border-blue-8",
            )}
            style={{
              borderRadius: PHONE_BORDER_RADIUS,
            }}
          ></div>
        </AutoLayout>
      </AutoLayout>
    </AutoLayout>
  );
};

const PhoneOutlineInner: FC<Props & ScrollScreenSwitcherEventHandlerArgs> = (
  props,
) => {
  const {
    scrollViewRef,
    onScroll,
    onWheel,
    extraHeightToMakeScrollCaptureWork,
    isScrolledToBottom,
    isScrolledToTop,
  } = useSwipeUpAndDownBetweenScreens(props);
  const phoneHeight = props.phoneHeight ?? DEFAULT_PHONE_HEIGHT;
  return (
    <AutoLayout
      alignSelf={"stretch"}
      flex={1}
      direction={"vertical"}
      style={{
        position: "relative",
      }}
    >
      {props.hideFadeOutToBlack ? null : (
        <FadeOutToBlack show={!isScrolledToTop} position={"top"} />
      )}
      <AutoLayout
        ref={scrollViewRef}
        direction={"vertical"}
        alignSelf={"stretch"}
        flex={1}
        style={{
          position: "relative",
          overflowY: "auto",
          overscrollBehaviorY: "contain",
        }}
        onWheel={onWheel}
        onScroll={onScroll}
        hideScrollbar={true}
      >
        <AutoLayout
          direction={"vertical"}
          alignSelf={"stretch"}
          height={phoneHeight + extraHeightToMakeScrollCaptureWork}
          style={{
            minHeight: phoneHeight + extraHeightToMakeScrollCaptureWork,
          }}
        >
          {props.content}
        </AutoLayout>
      </AutoLayout>
      {props.hideFadeOutToBlack ? null : (
        <FadeOutToBlack show={!isScrolledToBottom} position={"bottom"} />
      )}
    </AutoLayout>
  );
};

export default PhoneOutline;

export const PHONE_OUTLINE_ANIMATION_DURATION_SEC = 0.3;
export const PHONE_OUTLINE_ANIMATION_TRANSITION: React.ComponentProps<
  typeof motion.div
>["transition"] = {
  duration: PHONE_OUTLINE_ANIMATION_DURATION_SEC,
  type: "cubic-bezier",
};
